export const NavigationEvents = {
  PAGE_VIEWED: 'Page Viewed',
} as const

export const CaptchaEvents = {
  /** Fires whenever the widget renders — the denominator for the failure rate,
   *  and where the load-time distribution comes from. */
  LOADED: 'Captcha Loaded',
  FAILED: 'Captcha Failed',
  /** Fires when a submit-time run escalates to a visible interactive
   *  challenge — how often real users actually see the captcha. */
  CHALLENGED: 'Captcha Challenged',
  /** The user solved a visible challenge. Pairs with CHALLENGED to give the
   *  solve rate, and carries how long the human took. */
  RESOLVED: 'Captcha Resolved',
  /** The user closed a visible challenge instead of solving it — the
   *  abandonment rate of CHALLENGED. */
  DISMISSED: 'Captcha Dismissed',
} as const

export const AuthEvents = {
  USER_LOGGED_IN: 'User Logged In',
  USER_LOGGED_OUT: 'User Logged Out',
  ACCOUNT_SWITCHED: 'Account Switched',
} as const

export const EnvelopeEvents = {
  WIZARD_STARTED: 'Envelope Wizard Started',
  EDIT_STARTED: 'Envelope Edit Started',
  STEP_VIEWED: 'Envelope Step Viewed',
  DOCUMENT_UPLOADED: 'Envelope Document Uploaded',
  DOCUMENT_REMOVED: 'Envelope Document Removed',
  SIGNER_ADDED: 'Envelope Signer Added',
  SIGNER_REMOVED: 'Envelope Signer Removed',
  SIGN_MARK_PLACED: 'Envelope Sign Mark Placed',
  SAVED: 'Envelope Saved',
  PUBLISHED: 'Envelope Published',
  PUBLISH_FAILED: 'Envelope Publish Failed',
  SAVE_AND_EXIT: 'Envelope Save & Exit',
  CONTACT_PICKER_OPENED: 'Envelope Contact Picker Opened',
  SIGNER_ADDED_FROM_CONTACT: 'Envelope Signer Added From Contact',
  SELF_SIGN_CLICKED: 'Envelope Self Sign Clicked',
  SIGNER_EDITED: 'Envelope Signer Edited',
  SIGN_MARK_REMOVED: 'Envelope Sign Mark Removed',
  SIGN_MARK_EDITED: 'Envelope Sign Mark Edited',
  SIGN_MARK_DUPLICATED: 'Envelope Sign Mark Duplicated',
  SIGN_MARK_SIGNER_CHANGED: 'Envelope Sign Mark Signer Changed',
  DOCUMENT_EDITED: 'Envelope Document Edited',
  DOCUMENT_SWITCHED: 'Envelope Document Switched',
  ZOOM_CHANGED: 'Envelope Zoom Changed',
  WIZARD_ABANDONED: 'Envelope Wizard Abandoned',
  SAVE_FAILED: 'Envelope Save Failed',
  SAVE_ERROR_DIALOG_VIEWED: 'Envelope Save Error Dialog Viewed',
  AUTO_SAVE_INFO_VIEWED: 'Envelope Auto Save Info Viewed',
  LIST_BULK_ACTION: 'Envelope List Bulk Action',
} as const

export const EnvelopeDetailEvents = {
  PAGE_VIEWED: 'Envelope Detail Page Viewed',
  DOCUMENT_OPENED: 'Envelope Detail Document Opened',
  DOCUMENT_DOWNLOADED: 'Envelope Detail Document Downloaded',
  CERTIFICATE_OPENED: 'Envelope Detail Certificate Opened',
  CERTIFICATE_DOWNLOADED: 'Envelope Detail Certificate Downloaded',
  SIGNER_LINK_COPIED: 'Envelope Detail Signer Link Copied',
  SIGNER_LINK_OPENED: 'Envelope Detail Signer Link Opened',
  SIGNER_DETAIL_OPENED: 'Envelope Detail Signer Detail Opened',
  ACTIVITY_DETAIL_OPENED: 'Envelope Detail Activity Detail Opened',
  ACCESS_DETAIL_OPENED: 'Envelope Detail Access Detail Opened',
  ACTION_PUBLISH: 'Envelope Detail Action Publish',
  ACTION_CANCEL: 'Envelope Detail Action Cancel',
  ACTION_DELETE: 'Envelope Detail Action Delete',
  ACTION_EDIT: 'Envelope Detail Action Edit',
  ACTION_HOLD: 'Envelope Detail Action Hold',
  ACTION_RESTORE: 'Envelope Detail Action Restore',
  ACTION_REINVITE: 'Envelope Detail Action Reinvite',
  ACTION_UNSCHEDULE: 'Envelope Detail Action Unschedule',
} as const

export const FeaturePaywallEvents = {
  OPENED: 'Feature Paywall Opened',
  CLOSED: 'Feature Paywall Closed',
  CONVERTED: 'Feature Paywall Converted',
  BLOCKED: 'Feature Paywall Blocked',
  BADGE_CLICKED: 'Feature Paywall Badge Clicked',
  BADGE_VIEWED: 'Feature Paywall Badge Viewed',
} as const

export const PlanPaywallEvents = {
  OPENED: 'Plan Paywall Opened',
  CONVERTED: 'Plan Paywall Converted',
  LOGOUT_CLICKED: 'Plan Paywall Logout Clicked',
  HOME_CLICKED: 'Plan Paywall Home Clicked',
} as const

export const Enforce2FaEvents = {
  /** Fires when the blocking modal opens for a user of an account that
   *  enforces 2FA while the user has none configured. */
  MODAL_VIEWED: 'Enforce 2FA Modal Viewed',
  CONTINUE_CLICKED: 'Enforce 2FA Continue Clicked',
  LOGOUT_CLICKED: 'Enforce 2FA Logout Clicked',
  SETUP_COMPLETED: 'Enforce 2FA Setup Completed',
  ADMIN_DIALOG_VIEWED: 'Enforce 2FA Admin Dialog Viewed',
  ADMIN_DIALOG_DISMISSED: 'Enforce 2FA Admin Dialog Dismissed',
  ADMIN_SETUP_COMPLETED: 'Enforce 2FA Admin Setup Completed',
} as const

export const SandboxEvents = {
  BANNER_VIEWED: 'Sandbox Banner Viewed',
  BANNER_CLICKED: 'Sandbox Banner Clicked',
  DIALOG_OPENED: 'Sandbox Dialog Opened',
  DIALOG_CLOSED: 'Sandbox Dialog Closed',
} as const

export const SearchEvents = {
  OPENED: 'Search Opened',
} as const

export const FreePlanBannerEvents = {
  VIEWED: 'Free Plan Banner Viewed',
  CLICKED: 'Free Plan Banner Clicked',
} as const

export const ReactivateBannerEvents = {
  VIEWED: 'Reactivate Banner Viewed',
  CLICKED: 'Reactivate Banner Clicked',
} as const

export const NoPlanBannerEvents = {
  VIEWED: 'No Plan Banner Viewed',
  CLICKED: 'No Plan Banner Clicked',
} as const

export const UnsubscribeEvents = {
  PAGE_VIEWED: 'Unsubscribe Page Viewed',
  SUBMITTED: 'Unsubscribe Submitted',
  SUCCESS: 'Unsubscribe Success',
  FAILED: 'Unsubscribe Failed',
} as const

export const ViewEvents = {
  PAGE_VIEWED: 'View Page Viewed',
  ERROR_PAGE_VIEWED: 'View Error Page Viewed',
  DOCUMENT_VIEWED: 'View Document Viewed',
  DOCUMENT_DOWNLOADED: 'View Document Downloaded',
  DOCUMENT_DOWNLOAD_FAILED: 'View Document Download Failed',
  DOCUMENT_DETAIL_OPENED: 'View Document Detail Opened',
  CERTIFICATE_VIEWED: 'View Certificate Viewed',
  CERTIFICATE_DOWNLOADED: 'View Certificate Downloaded',
  STATUS_DIALOG_VIEWED: 'View Status Dialog Viewed',
  ACTION_HISTORY_OPENED: 'View Action History Opened',
  ACCESSES_OPENED: 'View Accesses Opened',
  ENVELOPE_DETAILS_OPENED: 'View Envelope Details Opened',
  MINIMAP_PAGE_CLICKED: 'View Minimap Page Clicked',
  MOBILE_DRAWER_OPENED: 'View Mobile Drawer Opened',
  SANDBOX_BANNER_VIEWED: 'View Sandbox Banner Viewed',
  SANDBOX_BANNER_CLICKED: 'View Sandbox Banner Clicked',
} as const

export const VerifyEvents = {
  PAGE_VIEWED: 'Verify Page Viewed',
  METHOD_SELECTED: 'Verify Method Selected',
  IDENTIFIER_SUBMITTED: 'Verify Identifier Submitted',
  FILE_HASHED: 'Verify File Hashed',
  FILE_ANALYZED: 'Verify File Analyzed',
  ENVELOPE_OPENED: 'Verify Envelope Opened',
} as const

export const ReviewEvents = {
  PAGE_VIEWED: 'Review Page Viewed',
  CONSENT_GIVEN: 'Review Consent Given',
  STEP_VIEWED: 'Review Step Viewed',
  DOCUMENT_VIEWED: 'Review Document Viewed',
  DOCUMENT_DOWNLOADED: 'Review Document Downloaded',
  MARK_CLICKED: 'Review Mark Clicked',
  MARK_CONFIRMED: 'Review Mark Confirmed',
  MARK_CAPTURE_OPENED: 'Review Mark Capture Opened',
  MARK_CAPTURE_METHOD: 'Review Mark Capture Method',
  FIELD_FILLED: 'Review Field Filled',
  MFA_STEP_STARTED: 'Review MFA Step Started',
  MFA_STEP_COMPLETED: 'Review MFA Step Completed',
  APPROVED: 'Review Approved',
  APPROVE_FAILED: 'Review Approve Failed',
  REJECTED: 'Review Rejected',
  DECLINED: 'Review Declined',
  SIGNED_ENVELOPE_VIEWED: 'Review Signed Envelope Viewed',
  SANDBOX_BANNER_VIEWED: 'Review Sandbox Banner Viewed',
  SANDBOX_BANNER_CLICKED: 'Review Sandbox Banner Clicked',
  OTP_CODE_SENT: 'Review OTP Code Sent',
  SELFIE_MODE_CHOSEN: 'Review Selfie Mode Chosen',
  SELFIE_LIVENESS_COMPLETED: 'Review Selfie Liveness Completed',
  PIX_QR_GENERATED: 'Review PIX QR Generated',
  PIX_EXPIRED: 'Review PIX Expired',
  DIGITAL_CERTIFICATE_UPLOADED: 'Review Digital Certificate Uploaded',
  DIGITAL_CERTIFICATE_UPLOAD_FAILED: 'Review Digital Certificate Upload Failed',
  GEOLOCATION_DENIED: 'Review Geolocation Denied',
  OTP_SEND_FAILED: 'Review OTP Send Failed',
  SELFIE_CAMERA_FAILED: 'Review Selfie Camera Failed',
  SELFIE_HEAD_POSE_COMPLETED: 'Review Selfie Head Pose Completed',
  SELFIE_LOW_LIGHT_DETECTED: 'Review Selfie Low Light Detected',
  CONTINUE_NUDGE: 'Review Continue Nudge',
  NEXT_MARK_INDICATOR_SHOWN: 'Review Next Mark Indicator Shown',
  NEXT_MARK_INDICATOR_CLICKED: 'Review Next Mark Indicator Clicked',
  MARK_REMOVED: 'Review Mark Removed',
  PIX_COPY_CODE: 'Review PIX Copy Code',
  DOCUMENT_DOWNLOAD_FAILED: 'Review Document Download Failed',
  MERCHANT_BANNER_VIEWED: 'Review Merchant Banner Viewed',
  MERCHANT_BANNER_CLICKED: 'Review Merchant Banner Clicked',
  SATISFACTION_VIEWED: 'Review Satisfaction Viewed',
  SATISFACTION_RATED: 'Review Satisfaction Rated',
  SATISFACTION_DISMISSED: 'Review Satisfaction Dismissed',
  ATTACHMENT_UPLOADED: 'Review Attachment Uploaded',
  ATTACHMENT_REMOVED: 'Review Attachment Removed',
} as const

export const VersionEvents = {
  UPDATE_DIALOG_SHOWN: 'Version Update Dialog Shown',
  UPDATE_DIALOG_ACCEPTED: 'Version Update Dialog Accepted',
  UPDATE_DIALOG_DISMISSED: 'Version Update Dialog Dismissed',
  ERROR_PAGE_RENDERED: 'Version Error Page Rendered',
  ERROR_PAGE_UPDATE_CLICKED: 'Version Error Page Update Clicked',
  ERROR_PAGE_RETRY_CLICKED: 'Version Error Page Retry Clicked',
} as const

export const DashboardEvents = {
  DATE_RANGE_CHANGED: 'Dashboard Date Range Changed',
  STAT_CARD_CLICKED: 'Dashboard Stat Card Clicked',
  CREATE_ENVELOPE_CLICKED: 'Dashboard Create Envelope Clicked',
} as const

export const BillingEvents = {
  PAGE_VIEWED: 'Billing Plans Page Viewed',
  RECURRENCE_CHANGED: 'Billing Recurrence Changed',
  PLAN_SELECTED: 'Billing Plan Selected',
  CHECKOUT_STARTED: 'Billing Checkout Started',
  PLAN_UPGRADED: 'Billing Plan Upgraded',
  PLAN_DOWNGRADED: 'Billing Plan Downgraded',
  PLAN_CHANGE_COMPLETED: 'Billing Plan Change Completed',
  PLAN_CANCELLED: 'Billing Plan Cancelled',
  PLAN_REACTIVATED: 'Billing Plan Reactivated',
  ADDITIONAL_ENVELOPES_UPDATED: 'Billing Additional Envelopes Updated',
  STANDALONE_ENVELOPES_PURCHASED: 'Billing Standalone Envelopes Purchased',
  PAYMENT_METHOD_UPDATED: 'Billing Payment Method Updated',
  PENDING_INVOICE_PAID: 'Billing Pending Invoice Paid',
  PLANS_ABANDONED: 'Billing Plans Abandoned',
  SUBSCRIPTION_ACTIVATED: 'Billing Subscription Activated',
  ACTIVATION_CTA_CLICKED: 'Billing Activation Cta Clicked',
  ACTIVATION_TIMED_OUT: 'Billing Activation Timed Out',
} as const

export const ContextualUpsellEvents = {
  OPENED: 'Contextual Upsell Opened',
  CLOSED: 'Contextual Upsell Closed',
  CONVERTED: 'Contextual Upsell Converted',
} as const

export const TemplateEvents = {
  CREATED: 'Template Created',
  UPDATED: 'Template Updated',
  REMOVED: 'Template Removed',
  FULFILLMENT_GENERATED: 'Template Fulfillment Generated',
  DETAIL_PAGE_VIEWED: 'Template Detail Page Viewed',
  FIELD_ADDED: 'Template Field Added',
  FIELD_REMOVED: 'Template Field Removed',
  FIELD_REORDERED: 'Template Field Reordered',
  FIELD_DUPLICATED: 'Template Field Duplicated',
  FIELD_INSERTED_BY_DRAG: 'Template Field Inserted By Drag',
  FIELD_ALIAS_RENAMED: 'Template Field Alias Renamed',
} as const

export const PlanFinderEvents = {
  STARTED: 'Plan Finder Started',
  USAGE_SELECTED: 'Plan Finder Usage Selected',
  ENVELOPES_SELECTED: 'Plan Finder Envelopes Selected',
  FEATURES_SELECTED: 'Plan Finder Features Selected',
  PLAN_SELECTED: 'Plan Finder Plan Selected',
  CLOSED: 'Plan Finder Closed',
} as const

export const PlanFinderBannerEvents = {
  VIEWED: 'Plan Finder Banner Viewed',
  CLICKED: 'Plan Finder Banner Clicked',
} as const

export const OnboardingSetupEvents = {
  MODAL_VIEWED: 'Onboarding Modal Viewed',
  PROFILE_COMPLETED: 'Onboarding Profile Completed',
  TOUR_CHOSEN: 'Onboarding Tour Chosen',
} as const

export const CreateAccountEvents = {
  WIZARD_OPENED: 'Create Account Wizard Opened',
  STEP_VIEWED: 'Create Account Step Viewed',
  STEP_COMPLETED: 'Create Account Step Completed',
  WIZARD_COMPLETED: 'Create Account Wizard Completed',
  WIZARD_ABANDONED: 'Create Account Wizard Abandoned',
} as const

export const AffiliateEvents = {
  /** Fires whenever the sidebar's activation dialog opens — the denominator
   *  PROGRAM_JOINED needs before a join rate can be computed at all. */
  ACTIVATION_DIALOG_VIEWED: 'Affiliate Activation Dialog Viewed',
  ACTIVATION_DIALOG_DISMISSED: 'Affiliate Activation Dialog Dismissed',
  PROGRAM_JOINED: 'Affiliate Program Joined',
  WELCOME_DIALOG_VIEWED: 'Affiliate Welcome Dialog Viewed',
  ANALYTICS_FILTERS_APPLIED: 'Affiliate Analytics Filters Applied',
  CODE_CREATED: 'Affiliate Code Created',
  WITHDRAWAL_REQUESTED: 'Affiliate Withdrawal Requested',
  LINK_COPIED: 'Affiliate Link Copied',
  COUPON_COPIED: 'Affiliate Coupon Copied',
  OVERVIEW_SHORTCUT_CLICKED: 'Affiliate Overview Shortcut Clicked',
  CUSTOMER_TIMELINE_OPENED: 'Affiliate Customer Timeline Opened',
} as const

export const ReportEvents = {
  VIEWED: 'Report Viewed',
  FILTERS_APPLIED: 'Report Filters Applied',
  FILTERS_CLEARED: 'Report Filters Cleared',
  EXPORT_FAILED: 'Report Export Failed',
  CHART_EXPANDED: 'Report Chart Expanded',
  CHART_SAVED: 'Report Chart Saved',
  TABLE_EXPANDED: 'Report Table Expanded',
  TABLE_SAVED: 'Report Table Saved',
  REFRESHED: 'Report Refreshed',
  AUTO_REFRESH_CHANGED: 'Report Auto Refresh Changed',
} as const

export const BulkSignEvents = {
  SESSION_STARTED: 'Bulk Sign Session Started',
  SESSION_RESUMED: 'Bulk Sign Session Resumed',
  FACTOR_ADOPTED: 'Bulk Sign Factor Adopted',
  FACTOR_DEFERRED: 'Bulk Sign Factor Deferred',
  ENVELOPE_SIGNED: 'Bulk Sign Envelope Signed',
  ALL_STARTED: 'Bulk Sign All Started',
  ALL_FINISHED: 'Bulk Sign All Finished',
  SESSION_ENDED: 'Bulk Sign Session Ended',
  SESSION_FINISHED: 'Bulk Sign Session Finished',
} as const

export const MessagingChannelsEvents = {
  WHATSAPP_CONNECT_STARTED: 'Messaging Channels WhatsApp Connect Started',
  WHATSAPP_CONNECT_COMPLETED: 'Messaging Channels WhatsApp Connect Completed',
  WHATSAPP_CONNECT_CANCELLED: 'Messaging Channels WhatsApp Connect Cancelled',
  WHATSAPP_CONNECT_FAILED: 'Messaging Channels WhatsApp Connect Failed',
} as const

export const AiChatEvents = {
  OPENED: 'AI Chat Opened',
  QUESTION_SENT: 'AI Chat Question Sent',
  ANSWER_COMPLETED: 'AI Chat Answer Completed',
  LIMIT_REACHED: 'AI Chat Limit Reached',
  FAILED: 'AI Chat Failed',
} as const

export const McpEvents = {
  WIZARD_OPENED: 'MCP Wizard Opened',
  CLIENT_PICKED: 'MCP Client Picked',
  TOKEN_CREATED: 'MCP Token Created',
  CONNECTOR_URL_COPIED: 'MCP Connector URL Copied',
  CONSENT_APPROVED: 'MCP Consent Approved',
  CONSENT_DENIED: 'MCP Consent Denied',
} as const
